import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { Upload, Settings, Users, Database, Calendar, Shield, RefreshCw, ShieldAlert, Layers, Coins, Plus, Trash2, Edit3, Save, X, Check, ChevronDown, ChevronRight, Eye, EyeOff, Copy, UserPlus, Download, HardDrive } from 'lucide-react';
import { T, fmtMonth, safeNum } from '../theme';
import { Card, StatCard } from '../components/UI';
import RiskSettings from './RiskSettings';
import GroupSettings from './GroupSettings';
import { ReportDesigner, getReportConfigs, saveReportConfigs } from './Reports';
import { db, firebaseConfig } from '../firebase';
import { collection, getDocs, doc, setDoc, updateDoc } from 'firebase/firestore';

const CO = { salam: { name: "Al Salam", color: "#d4a017", logo: "/logo-salam.png" }, river: { name: "River Prime", color: "#3b82f6", logo: "/logo-river.png" } };

// All localStorage keys used by the system
const BACKUP_KEYS = [
  { key: "cp_users", label: "Users & Permissions", icon: "👤" },
  { key: "cp_monitor_watchlist", label: "Monitoring Watchlist", icon: "👁" },
  { key: "cp_monitor_notes", label: "Monitoring Notes & Comments", icon: "💬" },
  { key: "cp_covering_exclude", label: "Covering Gold Exclude List", icon: "🚫" },
  { key: "broker_risk_config", label: "Risk Score Settings", icon: "⚙️" },
  { key: "client_group_config", label: "Group Rules & Settings", icon: "📋" },
  { key: "cp_report_templates", label: "Report Templates", icon: "📊" },
  { key: "cp_system_exclude", label: "System Exclude List", icon: "🚫" },
];

const PAGE_STRUCTURE = [
  { id: "dashboard", label: "Dashboard", tabs: [] },
  { id: "profiles", label: "Client Profiles", tabs: [] },
  { id: "am", label: "Account Managers", tabs: [] },
  { id: "risk", label: "Risk Monitor", tabs: [
    { id: "groups", label: "Clients Group" },
    { id: "monitor", label: "Risk Monitor" },
    { id: "changes", label: "Group Changes" },
    { id: "evolution", label: "Risk Evolution" },
  ]},
  { id: "compare", label: "Comparison", tabs: [] },
  { id: "analytics", label: "Analytics", tabs: [
    { id: "overview", label: "Overview" },
    { id: "country", label: "Countries" },
    { id: "reactivation", label: "Reactivation & New" },
  ]},
  { id: "monitoring", label: "Monitoring", tabs: [] },
  { id: "covering", label: "Covering Gold", tabs: [
    { id: "overview", label: "Overview" },
    { id: "heatmap", label: "Heatmap" },
    { id: "clients", label: "Clients" },
    { id: "best", label: "Best Covering" },
    { id: "exclude", label: "Exclude" },
  ]},
];

export { PAGE_STRUCTURE };

const USERS_KEY = "cp_users";
const loadUsers = () => { try { return JSON.parse(localStorage.getItem(USERS_KEY) || "[]"); } catch { return []; } };
const saveUsers = list => localStorage.setItem(USERS_KEY, JSON.stringify(list));
const makeId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
const genPass = () => { const c = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789"; let p = ""; for (let i = 0; i < 10; i++) p += c[Math.floor(Math.random() * c.length)]; return p; };

async function createAuthUser(email, password) {
  const res = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${firebaseConfig.apiKey}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, returnSecureToken: false }),
  });
  const data = await res.json();
  if (!res.ok) {
    const code = data?.error?.message || 'UNKNOWN';
    const map = {
      EMAIL_EXISTS: 'Email already exists.',
      INVALID_EMAIL: 'Invalid email format.',
      WEAK_PASSWORD: 'Password must be at least 6 characters.',
      OPERATION_NOT_ALLOWED: 'Email/password login is not enabled in Firebase Auth.',
    };
    throw new Error(map[code] || `Failed to create auth user (${code}).`);
  }
  return data;
}

const defaultUser = () => ({
  id: makeId(), name: "", email: "", password: "", role: "viewer",
  companies: ["salam", "river"],
  pages: PAGE_STRUCTURE.map(p => p.id),
  tabs: PAGE_STRUCTURE.reduce((acc, p) => { if (p.tabs.length) acc[p.id] = p.tabs.map(t => t.id); return acc; }, {}),
  agents: [],
  active: true, createdAt: new Date().toISOString(),
});

/* ═══ USER FORM ═══ */
function UserForm({ user, agentData, onSave, onCancel, isNew }) {
  const [form, setForm] = useState(() => ({ ...defaultUser(), ...user }));
  const [showPass, setShowPass] = useState(false);
  const [expandedPage, setExpandedPage] = useState(null);

  // Filter agents by selected companies
  const agents = useMemo(() => {
    if (!agentData) return [];
    return agentData.filter(a => {
      if (form.companies.length === 0) return false;
      // Show agent if they have clients in any of the selected companies
      return a.companies.some(c => form.companies.includes(c));
    }).map(a => a.name);
  }, [agentData, form.companies]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleCompany = id => set("companies", form.companies.includes(id) ? form.companies.filter(c => c !== id) : [...form.companies, id]);

  const togglePage = pageId => {
    const has = form.pages.includes(pageId);
    if (has) {
      const np = form.pages.filter(p => p !== pageId);
      const nt = { ...form.tabs }; delete nt[pageId];
      setForm(f => ({ ...f, pages: np, tabs: nt }));
    } else {
      const page = PAGE_STRUCTURE.find(p => p.id === pageId);
      const nt = { ...form.tabs };
      if (page.tabs.length) nt[pageId] = page.tabs.map(t => t.id);
      setForm(f => ({ ...f, pages: [...f.pages, pageId], tabs: nt }));
    }
  };

  const toggleTab = (pageId, tabId) => {
    const cur = form.tabs[pageId] || [];
    const has = cur.includes(tabId);
    const nt = { ...form.tabs };
    nt[pageId] = has ? cur.filter(t => t !== tabId) : [...cur, tabId];
    if (nt[pageId].length === 0) {
      delete nt[pageId];
      setForm(f => ({ ...f, pages: f.pages.filter(p => p !== pageId), tabs: nt }));
    } else {
      if (!form.pages.includes(pageId)) setForm(f => ({ ...f, pages: [...f.pages, pageId], tabs: nt }));
      else setForm(f => ({ ...f, tabs: nt }));
    }
  };

  const toggleAllTabs = pageId => {
    const page = PAGE_STRUCTURE.find(p => p.id === pageId);
    const cur = form.tabs[pageId] || [];
    const allOn = cur.length === page.tabs.length;
    const nt = { ...form.tabs };
    if (allOn) { delete nt[pageId]; setForm(f => ({ ...f, pages: f.pages.filter(p => p !== pageId), tabs: nt })); }
    else { nt[pageId] = page.tabs.map(t => t.id); setForm(f => ({ ...f, pages: f.pages.includes(pageId) ? f.pages : [...f.pages, pageId], tabs: nt })); }
  };

  const toggleAgent = name => {
    if (form.agents.length === 0) set("agents", [name]);
    else if (form.agents.includes(name)) set("agents", form.agents.filter(a => a !== name));
    else set("agents", [...form.agents, name]);
  };

  const selectAllPages = () => {
    const at = {}; PAGE_STRUCTURE.forEach(p => { if (p.tabs.length) at[p.id] = p.tabs.map(t => t.id); });
    setForm(f => ({ ...f, pages: PAGE_STRUCTURE.map(p => p.id), tabs: at }));
  };
  const deselectAllPages = () => setForm(f => ({ ...f, pages: [], tabs: {} }));

  const valid = form.name.trim() && form.email.trim() && (isNew ? form.password.trim().length >= 6 : true) && form.companies.length > 0 && form.pages.length > 0;
  const inp = { padding: "10px 12px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.bgInput, color: T.text, fontSize: 13, outline: "none", width: "100%" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{isNew ? "Create New User" : "Edit User"}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onCancel} style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${T.border}`, background: "transparent", color: T.textDim, cursor: "pointer", fontSize: 12 }}>Cancel</button>
          <button onClick={() => valid && onSave(form)} disabled={!valid}
            style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: valid ? T.cyan : T.border, color: valid ? "#000" : T.textMuted, cursor: valid ? "pointer" : "default", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
            <Save size={14} /> {isNew ? "Create User" : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Basic Info */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 14 }}>Basic Information</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div>
            <label style={{ fontSize: 10, color: T.textMuted, fontWeight: 600, textTransform: "uppercase", marginBottom: 4, display: "block" }}>Full Name *</label>
            <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Doaa Alshobaki" style={inp} />
          </div>
          <div>
            <label style={{ fontSize: 10, color: T.textMuted, fontWeight: 600, textTransform: "uppercase", marginBottom: 4, display: "block" }}>Email *</label>
            <input value={form.email} onChange={e => set("email", e.target.value)} placeholder="e.g. doaa@salaminv.com" style={inp} />
          </div>
          <div>
            <label style={{ fontSize: 10, color: T.textMuted, fontWeight: 600, textTransform: "uppercase", marginBottom: 4, display: "block" }}>{isNew ? "Password *" : "Password"}</label>
            {isNew ? (
              <div style={{ display: "flex", gap: 6 }}>
                <div style={{ position: "relative", flex: 1 }}>
                  <input type={showPass ? "text" : "password"} value={form.password} onChange={e => set("password", e.target.value)} placeholder="Min 6 characters" style={{ ...inp, paddingRight: 36 }} />
                  <button onClick={() => setShowPass(!showPass)} style={{ position: "absolute", right: 8, top: 8, background: "none", border: "none", color: T.textMuted, cursor: "pointer" }}>
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <button onClick={() => { set("password", genPass()); setShowPass(true); }}
                  style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${T.purple}40`, background: T.purple + "15", color: T.purple, fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                  🎲 Generate
                </button>
              </div>
            ) : (
              <div style={{ ...inp, color: T.textMuted }}>Password cannot be changed here.</div>
            )}
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 10, color: T.textMuted, fontWeight: 600, textTransform: "uppercase", marginBottom: 4, display: "block" }}>Role</label>
          <div style={{ display: "flex", gap: 8 }}>
            {[{ id: "admin", label: "Admin", desc: "Full access to everything", color: T.red },
              { id: "viewer", label: "Viewer", desc: "Restricted by permissions below", color: T.cyan }
            ].map(r => (
              <div key={r.id} onClick={() => set("role", r.id)}
                style={{ flex: 1, padding: "12px 14px", borderRadius: 8, border: `1px solid ${form.role === r.id ? r.color : T.border}`,
                  background: form.role === r.id ? r.color + "15" : "transparent", cursor: "pointer" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: form.role === r.id ? r.color : T.text, marginBottom: 2 }}>{r.label}</div>
                <div style={{ fontSize: 10, color: T.textMuted }}>{r.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {form.role === "viewer" && (<>
        {/* Company Access */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 14 }}>Company Access</div>
          <div style={{ display: "flex", gap: 12 }}>
            {[{ id: "salam", label: "Al Salam", color: "#d4a017" }, { id: "river", label: "River Prime", color: "#3b82f6" }].map(co => {
              const on = form.companies.includes(co.id);
              return (
                <div key={co.id} onClick={() => toggleCompany(co.id)}
                  style={{ flex: 1, padding: "14px 16px", borderRadius: 10, border: `2px solid ${on ? co.color : T.border}`,
                    background: on ? co.color + "15" : "transparent", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 22, height: 22, borderRadius: 5, border: `2px solid ${on ? co.color : T.border}`, background: on ? co.color : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {on && <Check size={14} color="#fff" />}
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: on ? co.color : T.textDim }}>{co.label}</span>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Pages & Tabs */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Pages & Tabs Access</div>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={selectAllPages} style={{ padding: "4px 10px", borderRadius: 5, border: `1px solid ${T.green}40`, background: T.green + "10", color: T.green, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Select All</button>
              <button onClick={deselectAllPages} style={{ padding: "4px 10px", borderRadius: 5, border: `1px solid ${T.red}40`, background: T.red + "10", color: T.red, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Deselect All</button>
            </div>
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            {PAGE_STRUCTURE.map(page => {
              const pageOn = form.pages.includes(page.id);
              const hasTabs = page.tabs.length > 0;
              const pageTabs = form.tabs[page.id] || [];
              const isExp = expandedPage === page.id;
              return (
                <div key={page.id} style={{ border: `1px solid ${pageOn ? T.cyan + "40" : T.border}30`, borderRadius: 8, overflow: "hidden" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: pageOn ? T.cyan + "08" : "transparent" }}>
                    <div onClick={() => hasTabs ? toggleAllTabs(page.id) : togglePage(page.id)}
                      style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${pageOn ? T.cyan : T.border}`, background: pageOn ? T.cyan : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                      {pageOn && <Check size={12} color="#000" />}
                    </div>
                    <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: pageOn ? T.text : T.textMuted, cursor: hasTabs ? "pointer" : "default" }}
                      onClick={() => hasTabs && setExpandedPage(isExp ? null : page.id)}>
                      {page.label}
                    </div>
                    {hasTabs && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 10, color: T.textMuted }}>{pageTabs.length}/{page.tabs.length} tabs</span>
                        <div onClick={() => setExpandedPage(isExp ? null : page.id)} style={{ cursor: "pointer", color: T.textMuted }}>
                          {isExp ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </div>
                      </div>
                    )}
                  </div>
                  {hasTabs && isExp && (
                    <div style={{ padding: "8px 14px 12px 46px", background: T.bg, borderTop: `1px solid ${T.border}20`, display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {page.tabs.map(t => {
                        const tabOn = pageTabs.includes(t.id);
                        return (
                          <div key={t.id} onClick={() => toggleTab(page.id, t.id)}
                            style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 6,
                              border: `1px solid ${tabOn ? T.cyan + "60" : T.border}`, background: tabOn ? T.cyan + "12" : "transparent", cursor: "pointer" }}>
                            <div style={{ width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${tabOn ? T.cyan : T.border}`, background: tabOn ? T.cyan : "transparent",
                              display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {tabOn && <Check size={9} color="#000" />}
                            </div>
                            <span style={{ fontSize: 11, color: tabOn ? T.text : T.textMuted, fontWeight: tabOn ? 600 : 400 }}>{t.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Sales Agent Filter */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Sales Agent Filter</div>
              <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>
                {form.agents.length === 0 ? "All agents — user sees all client data" : `${form.agents.length} agent${form.agents.length > 1 ? "s" : ""} — user only sees these agents' clients`}
              </div>
            </div>
            <button onClick={() => set("agents", [])}
              style={{ padding: "4px 10px", borderRadius: 5, border: `1px solid ${form.agents.length === 0 ? T.cyan + "40" : T.border}`, background: form.agents.length === 0 ? T.cyan + "15" : "transparent", color: form.agents.length === 0 ? T.cyan : T.textDim, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>
              {form.agents.length === 0 ? "✓ All Agents" : "Select All"}
            </button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {agents.map(name => {
              const explicit = form.agents.includes(name);
              const on = form.agents.length === 0 || explicit;
              return (
                <div key={name} onClick={() => toggleAgent(name)}
                  style={{ padding: "6px 12px", borderRadius: 6, border: `1px solid ${explicit ? T.purple + "60" : on ? T.border + "60" : T.border}`,
                    background: explicit ? T.purple + "15" : "transparent", cursor: "pointer" }}>
                  <span style={{ fontSize: 11, color: explicit ? T.purple : on ? T.textDim : T.textMuted, fontWeight: explicit ? 700 : 400 }}>{name}</span>
                </div>
              );
            })}
            {agents.length === 0 && <span style={{ fontSize: 11, color: T.textMuted }}>No agents found — upload company data first</span>}
          </div>
        </Card>
      </>)}

      {form.role === "admin" && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ padding: "20px", textAlign: "center" }}>
            <Shield size={32} color={T.red} style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Admin Role</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>Full access to all companies, pages, tabs, and data. No restrictions.</div>
          </div>
        </Card>
      )}
    </div>
  );
}

/* ═══ ADMIN PANEL ═══ */
export default function AdminPanel({ C, salamData, riverData, coveringSalam, coveringRiver, onCoveringUpload, onCoveringDeleteMonth, onReupload, onRiskConfigChange, showPhone, onTogglePhone, onExcludeChange }) {
  const [tab, setTab] = useState("overview");
  const [coverStatus, setCoverStatus] = useState({});
  const [coverLoading, setCoverLoading] = useState({});
  const coverRefSalam = useRef();
  const coverRefRiver = useRef();
  const [users, setUsers] = useState(loadUsers);
  const [usersLoading, setUsersLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [reportConfigs, setReportConfigs] = useState(getReportConfigs);
  const [editingReport, setEditingReport] = useState(null); // null = list, object = designer
  const [backupStatus, setBackupStatus] = useState(null);
  const importRef = useRef();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setUsersLoading(true);
      try {
        const snap = await getDocs(collection(db, 'users'));
        const remote = snap.docs.map(d => ({ id: d.id, ...d.data(), password: '' }));
        if (!cancelled) {
          setUsers(remote);
          saveUsers(remote);
        }
      } catch (e) {
        console.error('Failed to load users from Firestore:', e);
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── SYSTEM EXCLUDE ──
  const [excludeList, setExcludeList] = useState(() => { try { return JSON.parse(localStorage.getItem("cp_system_exclude") || "[]"); } catch { return []; } });
  const [excludeInput, setExcludeInput] = useState("");
  const [excludeType, setExcludeType] = useState("cu");
  const [excludeBulk, setExcludeBulk] = useState("");
  const saveExclude = useCallback((list) => { setExcludeList(list); localStorage.setItem("cp_system_exclude", JSON.stringify(list)); if (onExcludeChange) onExcludeChange(); }, [onExcludeChange]);
  const rawLookup = useMemo(() => {
    const map = {};
    [...(salamData || []), ...(riverData || [])].forEach(r => {
      const cu = String(r.CU_Account || ""); const login = String(r.Login || "");
      if (cu && !map["cu_" + cu]) map["cu_" + cu] = { name: r.Name, company: r._company, logins: new Set() };
      if (cu && map["cu_" + cu]) map["cu_" + cu].logins.add(login);
      if (login && !map["login_" + login]) map["login_" + login] = { name: r.Name, company: r._company, cu };
    });
    return map;
  }, [salamData, riverData]);

  const agentData = useMemo(() => (C?.amStats || []).filter(a => a.name).map(a => ({ name: a.name, companies: a._companies || [] })).sort((a,b) => a.name.localeCompare(b.name)), [C]);

  // ── BACKUP: Export all localStorage data as JSON ──
  const handleBackup = useCallback(() => {
    try {
      const backup = { _meta: { version: "2.0", exportedAt: new Date().toISOString(), app: "Clients Profiling" } };
      let count = 0;
      BACKUP_KEYS.forEach(({ key }) => {
        const val = localStorage.getItem(key);
        if (val) { backup[key] = JSON.parse(val); count++; }
      });
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CP-Backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setBackupStatus({ type: "success", msg: `✓ Backup exported — ${count} items saved` });
      setTimeout(() => setBackupStatus(null), 5000);
    } catch (err) {
      setBackupStatus({ type: "error", msg: `✗ Backup failed: ${err.message}` });
    }
  }, []);

  // ── RESTORE: Import backup JSON and overwrite localStorage ──
  const handleRestore = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (!data._meta) throw new Error("Invalid backup file — missing metadata");
        let count = 0;
        BACKUP_KEYS.forEach(({ key }) => {
          if (data[key] !== undefined) {
            localStorage.setItem(key, JSON.stringify(data[key]));
            count++;
          }
        });
        // Reload users state from restored data
        if (data["cp_users"]) setUsers(data["cp_users"]);
        setBackupStatus({ type: "success", msg: `✓ Restored ${count} items from backup (${data._meta.exportedAt?.slice(0, 10) || "unknown date"}). Reload page to apply all changes.` });
      } catch (err) {
        setBackupStatus({ type: "error", msg: `✗ Restore failed: ${err.message}` });
      }
    };
    reader.readAsText(file);
    if (e.target) e.target.value = "";
  }, []);

  const handleSaveUser = useCallback(async (user) => {
    try {
      const email = String(user.email || '').trim().toLowerCase();
      const baseProfile = {
        name: user.name,
        email,
        role: user.role,
        companies: user.role === 'admin' ? ['salam', 'river'] : (user.companies || []),
        pages: user.role === 'admin' ? PAGE_STRUCTURE.map(p => p.id) : (user.pages || []),
        tabs: user.role === 'admin'
          ? PAGE_STRUCTURE.reduce((acc, p) => { if (p.tabs.length) acc[p.id] = p.tabs.map(t => t.id); return acc; }, {})
          : (user.tabs || {}),
        agents: user.role === 'admin' ? [] : (user.agents || []),
        active: user.active !== false,
        updatedAt: new Date().toISOString(),
      };

      if (isNewUser) {
        const authRes = await createAuthUser(email, user.password);
        const uid = authRes.localId;
        const profile = { ...baseProfile, createdAt: new Date().toISOString() };
        await setDoc(doc(db, 'users', uid), profile);

        const localUser = { id: uid, ...profile, password: '' };
        setUsers(prev => {
          const next = [...prev, localUser];
          saveUsers(next);
          return next;
        });
      } else {
        const uid = user.id;
        await updateDoc(doc(db, 'users', uid), baseProfile);

        setUsers(prev => {
          const next = prev.map(u => u.id === uid ? { ...u, ...baseProfile, password: '' } : u);
          saveUsers(next);
          return next;
        });
      }

      setEditingUser(null);
      setIsNewUser(false);
    } catch (e) {
      alert(e.message || 'Failed to save user.');
    }
  }, [isNewUser]);

  const handleDeleteUser = useCallback(id => {
    if (!window.confirm("Delete this user?")) return;
    (async () => {
      try {
        await updateDoc(doc(db, 'users', id), { active: false, updatedAt: new Date().toISOString() });
      } catch (e) {
        console.error('Failed to disable user in Firestore:', e);
      }
      setUsers(prev => { const next = prev.filter(u => u.id !== id); saveUsers(next); return next; });
    })();
  }, []);

  const handleDuplicateUser = useCallback(user => {
    const dup = { ...user, id: makeId(), name: user.name + " (copy)", email: "", password: "", createdAt: new Date().toISOString(),
      tabs: { ...user.tabs }, companies: [...user.companies], pages: [...user.pages], agents: [...(user.agents || [])] };
    setEditingUser(dup); setIsNewUser(true);
  }, []);

  const handleCoverFile = async (companyId, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setCoverLoading(l => ({ ...l, [companyId]: true }));
    setCoverStatus(s => ({ ...s, [companyId]: `Parsing ${file.name}...` }));
    try {
      const result = await onCoveringUpload(companyId, file);
      setCoverStatus(s => ({ ...s, [companyId]: `✓ ${result.rows.toLocaleString()} trades loaded (${result.months.join(", ")})` }));
    } catch (err) { setCoverStatus(s => ({ ...s, [companyId]: `✗ Error: ${err.message || err}` })); }
    setCoverLoading(l => ({ ...l, [companyId]: false }));
    if (e.target) e.target.value = "";
  };

  const adminTabs = [
    { id: "overview", label: "Overview", icon: Database },
    { id: "users", label: "User Management", icon: Users },
    { id: "risk", label: "Risk Score Settings", icon: ShieldAlert },
    { id: "groups", label: "Group Settings", icon: Layers },
    { id: "covering", label: "Covering Gold", icon: Coins },
    { id: "exclude", label: "Exclude", icon: EyeOff },
    { id: "reports", label: "Reports", icon: Download },
  ];

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 16 }}>Admin Panel</h1>
      <div style={{ display: "flex", gap: 6, marginBottom: 20, borderBottom: `1px solid ${T.border}`, paddingBottom: 1 }}>
        {adminTabs.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); if (t.id !== "users") { setEditingUser(null); setIsNewUser(false); } }}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 18px", fontSize: 13, fontWeight: 600,
              background: tab === t.id ? T.bgCard : "transparent", color: tab === t.id ? T.cyan : T.textDim,
              border: "none", borderBottom: tab === t.id ? `2px solid ${T.cyan}` : "2px solid transparent",
              cursor: "pointer", borderRadius: "8px 8px 0 0" }}>
            <t.icon size={16} /> {t.label}
            {t.id === "users" && users.length > 0 && <span style={{ background: T.cyan + "20", color: T.cyan, padding: "1px 6px", borderRadius: 8, fontSize: 9, fontWeight: 700 }}>{users.length}</span>}
            {t.id === "exclude" && excludeList.length > 0 && <span style={{ background: "rgba(239,68,68,0.2)", color: "#ef4444", padding: "1px 6px", borderRadius: 8, fontSize: 9, fontWeight: 700 }}>{excludeList.length}</span>}
          </button>
        ))}
      </div>

      {tab === "risk" && <RiskSettings onConfigChange={onRiskConfigChange} />}
      {tab === "groups" && <GroupSettings onConfigChange={onRiskConfigChange} />}

      {/* ═══ OVERVIEW ═══ */}
      {tab === "overview" && (<>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 20 }}>
          <StatCard icon={<Database size={20} />} label="Total Records" value={C.latest.length.toLocaleString()} sub="Latest month" color={T.cyan} />
          <StatCard icon={<Users size={20} />} label="Unique Clients" value={C.clients.length.toLocaleString()} color={T.green} />
          <StatCard icon={<Calendar size={20} />} label="Months Loaded" value={C.months.length} sub={`${fmtMonth(C.months[0])} → ${fmtMonth(C.latestMonth)}`} color={T.blue} />
          <StatCard icon={<Shield size={20} />} label="Account Managers" value={C.amStats.length} color={T.purple} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
          {["salam", "river"].map(id => {
            const data = id === "salam" ? salamData : riverData; const co = CO[id]; const loaded = !!data;
            return (
              <Card key={id} accent={co.color}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <img src={co.logo} alt={co.name} style={{ height: 32, width: 32, objectFit: "contain", borderRadius: 6 }} onError={e => e.target.style.display = "none"} />
                    <div><div style={{ fontSize: 16, fontWeight: 700, color: co.color }}>{co.name}</div><div style={{ fontSize: 11, color: T.textMuted }}>{loaded ? `${data.length.toLocaleString()} rows loaded` : "Not loaded"}</div></div>
                  </div>
                  <button onClick={() => onReupload(id)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: `1px solid ${co.color}40`, background: loaded ? "transparent" : co.color + "20", color: co.color, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                    {loaded ? <><RefreshCw size={14} /> Re-upload</> : <><Upload size={14} /> Upload</>}
                  </button>
                </div>
                {loaded && (<div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                  {[{ label: "Rows", value: data.length.toLocaleString() }, { label: "Months", value: [...new Set(data.map(r => r.MonthKey))].filter(Boolean).length }, { label: "Columns", value: Object.keys(data[0] || {}).length }].map(m => (
                    <div key={m.label} style={{ padding: "8px 10px", background: T.bg, borderRadius: 6 }}><div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase" }}>{m.label}</div><div style={{ fontSize: 16, fontWeight: 700, color: co.color }}>{m.value}</div></div>
                  ))}</div>)}
              </Card>
            );
          })}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Card accent={T.green}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}><Settings size={20} color={T.cyan} /><span style={{ fontSize: 16, fontWeight: 600 }}>System Info</span></div>
            {[["Version", "2.0 (Dual Company)"], ["Data Period", `${fmtMonth(C.months[0])} → ${fmtMonth(C.latestMonth)}`], ["Months Covered", C.months.length],
              ["Active Clients", C.active], ["Dormant Clients", C.dormant], ["High Risk Clients", C.highRisk], ["Equity Drops >50%", C.equityDrops.length],
              ["VIP Clients", C.vipClients.length], ["Reactivated", C.reactivated.length], ["Avg Broker Risk", C.avgHealth + "/100"], ["Countries", C.countryStats.length], ["Registered Users", users.length],
            ].map(([k, v], i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${T.border}20`, fontSize: 13 }}>
                <span style={{ color: T.textDim }}>{k}</span><span className="mono" style={{ fontWeight: 600, color: T.cyan }}>{v}</span>
              </div>
            ))}
          </Card>
          <Card accent={T.amber}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Data Quality Check</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 }}>
              {[{ label: "Has Country", count: C.clients.filter(c => c.Country && c.Country !== "NULL").length },
                { label: "Has A.M", count: C.clients.filter(c => c["A.M"] && c["A.M"] !== "NULL" && c["A.M"] !== "X").length },
                { label: "Has Phone", count: C.clients.filter(c => c.Phone_Number && c.Phone_Number !== "NULL").length },
                { label: "Has Lifetime", count: C.clients.filter(c => c.LifetimeDays && c.LifetimeDays !== "NULL").length },
                { label: "Has Equity Prev", count: C.clients.filter(c => c.EquityPrevMonth && c.EquityPrevMonth !== "NULL").length },
                { label: "Has Volume", count: C.clients.filter(c => safeNum(c.Volume) > 0).length },
              ].map(d => { const pct = C.clients.length > 0 ? d.count / C.clients.length : 0;
                return (<div key={d.label}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4 }}>
                    <span style={{ color: T.textDim }}>{d.label}</span><span style={{ color: pct > 0.8 ? T.green : pct > 0.5 ? T.amber : T.red }}>{(pct * 100).toFixed(0)}%</span>
                  </div>
                  <div style={{ height: 4, background: T.bgHover, borderRadius: 2 }}><div style={{ height: "100%", width: (pct * 100) + "%", background: pct > 0.8 ? T.green : pct > 0.5 ? T.amber : T.red, borderRadius: 2 }} /></div>
                </div>);
              })}
            </div>
          </Card>
        </div>

        {/* ═══ PRIVACY SETTINGS ═══ */}
        <Card accent={T.purple} style={{ marginTop: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <Shield size={20} color={T.purple} />
            <span style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Privacy Settings</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderRadius: 10, border: `1px solid ${T.border}`, background: T.bgHover }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Show Phone Numbers</div>
              <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>When enabled, client phone numbers will be visible in Client Profiles</div>
            </div>
            <div onClick={() => onTogglePhone(!showPhone)}
              style={{ width: 44, height: 24, borderRadius: 12, background: showPhone ? T.green : T.border, cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
              <div style={{ width: 18, height: 18, borderRadius: 9, background: "#fff", position: "absolute", top: 3, left: showPhone ? 23 : 3, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
            </div>
          </div>
        </Card>

        {/* ═══ BACKUP & RESTORE ═══ */}
        <Card accent={T.cyan} style={{ marginTop: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <HardDrive size={20} color={T.cyan} />
            <span style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Backup & Restore</span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            {/* Export */}
            <div style={{ padding: 20, borderRadius: 10, border: `1px solid ${T.green}30`, background: T.green + "08" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <Download size={18} color={T.green} />
                <span style={{ fontSize: 14, fontWeight: 700, color: T.green }}>Export Backup</span>
              </div>
              <div style={{ fontSize: 11, color: T.textDim, marginBottom: 14, lineHeight: 1.6 }}>
                Downloads a JSON file containing all your system settings, users, monitoring data, and configurations. Save this file in a safe location.
              </div>
              <button onClick={handleBackup}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 24px", borderRadius: 8, border: "none",
                  background: T.green, color: "#000", fontSize: 13, fontWeight: 700, cursor: "pointer", width: "100%", justifyContent: "center" }}>
                <Download size={16} /> Download Full Backup
              </button>
            </div>

            {/* Import */}
            <div style={{ padding: 20, borderRadius: 10, border: `1px solid ${T.amber}30`, background: T.amber + "08" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <Upload size={18} color={T.amber} />
                <span style={{ fontSize: 14, fontWeight: 700, color: T.amber }}>Import Backup</span>
              </div>
              <div style={{ fontSize: 11, color: T.textDim, marginBottom: 14, lineHeight: 1.6 }}>
                Restore all settings from a previously exported backup file. This will <span style={{ color: T.red, fontWeight: 600 }}>overwrite</span> current data. Reload after import.
              </div>
              <button onClick={() => importRef.current?.click()}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 24px", borderRadius: 8, border: `2px solid ${T.amber}`,
                  background: "transparent", color: T.amber, fontSize: 13, fontWeight: 700, cursor: "pointer", width: "100%", justifyContent: "center" }}>
                <Upload size={16} /> Upload Backup File
              </button>
              <input ref={importRef} type="file" accept=".json" onChange={handleRestore} style={{ display: "none" }} />
            </div>
          </div>

          {/* Status message */}
          {backupStatus && (
            <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14,
              background: backupStatus.type === "success" ? T.green + "15" : T.red + "15",
              border: `1px solid ${backupStatus.type === "success" ? T.green : T.red}30`,
              color: backupStatus.type === "success" ? T.green : T.red, fontSize: 12, fontWeight: 600 }}>
              {backupStatus.msg}
            </div>
          )}

          {/* What's included */}
          <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", marginBottom: 8, letterSpacing: 0.5 }}>What's included in backup:</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
            {BACKUP_KEYS.map(({ key, label, icon }) => {
              const val = localStorage.getItem(key);
              const has = !!val;
              let count = "";
              try {
                const parsed = JSON.parse(val);
                if (Array.isArray(parsed)) count = `${parsed.length} items`;
                else if (typeof parsed === "object" && parsed) count = `${Object.keys(parsed).length} entries`;
              } catch {}
              return (
                <div key={key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 6,
                  background: has ? T.cyan + "08" : T.bg, border: `1px solid ${has ? T.cyan + "20" : T.border}20` }}>
                  <span style={{ fontSize: 16 }}>{icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: has ? T.text : T.textMuted }}>{label}</div>
                    <div style={{ fontSize: 9, color: T.textMuted }}>{has ? count || "saved" : "empty"}</div>
                  </div>
                  <div style={{ width: 8, height: 8, borderRadius: 4, background: has ? T.green : T.border }} />
                </div>
              );
            })}
          </div>
        </Card>
      </>)}

      {/* ═══ USER MANAGEMENT ═══ */}
      {tab === "users" && !editingUser && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>User Management</div>
              <div style={{ fontSize: 11, color: T.textDim }}>{users.length} user{users.length !== 1 ? "s" : ""} registered • Control access to companies, pages, tabs, and agents</div>
            </div>
            <button onClick={() => { setEditingUser(defaultUser()); setIsNewUser(true); }}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 20px", borderRadius: 8, border: "none", background: T.cyan, color: "#000", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              <UserPlus size={16} /> Create User
            </button>
          </div>
          {usersLoading && <div style={{ fontSize: 12, color: T.textDim, marginBottom: 10 }}>Loading users from Firebase...</div>}
          {users.length === 0 ? (
            <Card><div style={{ textAlign: "center", padding: "60px 20px" }}>
              <Users size={48} color={T.border} style={{ marginBottom: 16 }} />
              <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 6 }}>No Users Yet</div>
              <div style={{ fontSize: 12, color: T.textDim, maxWidth: 400, margin: "0 auto 16px" }}>Create users and assign specific access to companies, pages, tabs, and sales agents.</div>
              <button onClick={() => { setEditingUser(defaultUser()); setIsNewUser(true); }} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: T.cyan, color: "#000", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Create First User</button>
            </div></Card>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {users.map(user => {
                const pc = user.role === "admin" ? PAGE_STRUCTURE.length : user.pages.length;
                const coLabel = user.role === "admin" ? "All" : user.companies.map(c => c === "salam" ? "Salam" : "River").join(" + ") || "None";
                const agLabel = user.role === "admin" || user.agents.length === 0 ? "All" : user.agents.length + " agent" + (user.agents.length > 1 ? "s" : "");
                return (
                  <Card key={user.id}>
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 10, background: user.role === "admin" ? T.red + "20" : T.cyan + "20",
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <span style={{ fontSize: 18, fontWeight: 700, color: user.role === "admin" ? T.red : T.cyan }}>{(user.name || "?").charAt(0).toUpperCase()}</span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                          <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{user.name}</span>
                          <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, fontWeight: 700, background: user.role === "admin" ? T.red + "20" : T.cyan + "20", color: user.role === "admin" ? T.red : T.cyan }}>{user.role.toUpperCase()}</span>
                        </div>
                        <div style={{ fontSize: 11, color: T.textMuted }}>{user.email}</div>
                      </div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        {[{ label: coLabel, title: "Companies", color: "#d4a017" }, { label: `${pc} pages`, title: "Pages", color: T.cyan }, { label: agLabel, title: "Agents", color: T.purple }].map(b => (
                          <div key={b.title} style={{ textAlign: "center" }}>
                            <div style={{ fontSize: 8, color: T.textMuted, textTransform: "uppercase", fontWeight: 600, marginBottom: 2 }}>{b.title}</div>
                            <span style={{ padding: "3px 8px", borderRadius: 4, fontSize: 10, fontWeight: 600, background: b.color + "15", color: b.color }}>{b.label}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: "flex", gap: 4, marginLeft: 12 }}>
                        <button onClick={() => { setEditingUser({ ...user, tabs: { ...user.tabs }, companies: [...user.companies], pages: [...user.pages], agents: [...(user.agents || [])] }); setIsNewUser(false); }}
                          title="Edit" style={{ padding: 8, borderRadius: 6, border: `1px solid ${T.border}`, background: "transparent", color: T.cyan, cursor: "pointer" }}><Edit3 size={14} /></button>
                        <button onClick={() => handleDuplicateUser(user)} title="Duplicate"
                          style={{ padding: 8, borderRadius: 6, border: `1px solid ${T.border}`, background: "transparent", color: T.purple, cursor: "pointer" }}><Copy size={14} /></button>
                        <button onClick={() => handleDeleteUser(user.id)} title="Delete"
                          style={{ padding: 8, borderRadius: 6, border: `1px solid ${T.red}30`, background: "transparent", color: T.red, cursor: "pointer" }}><Trash2 size={14} /></button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}
      {tab === "users" && editingUser && (
        <UserForm user={editingUser} agentData={agentData} isNew={isNewUser} onSave={handleSaveUser} onCancel={() => { setEditingUser(null); setIsNewUser(false); }} />
      )}

      {/* ═══ COVERING GOLD UPLOAD ═══ */}
      {tab === "covering" && (<div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#fbbf24", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>🥇 Covering Gold Data Upload</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
          {["salam", "river"].map(id => {
            const data = id === "salam" ? coveringSalam : coveringRiver; const co = CO[id]; const ref = id === "salam" ? coverRefSalam : coverRefRiver;
            const loaded = data && data.length > 0;
            const logins = loaded ? [...new Set(data.map(r => r.Login))].filter(Boolean) : [];
            // Build month+year breakdown
            const monthBreakdown = loaded ? (() => {
              const map = {};
              data.forEach(r => { const k = `${r.Year}|${r.Month}`; if (!map[k]) map[k] = { year: r.Year, month: r.Month, trades: 0, clients: new Set() }; map[k].trades++; map[k].clients.add(r.Login); });
              return Object.values(map).sort((a, b) => String(a.year) === String(b.year) ? String(a.month).localeCompare(String(b.month)) : String(a.year).localeCompare(String(b.year)));
            })() : [];
            return (<Card key={id} accent={co.color}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <img src={co.logo} alt={co.name} style={{ height: 28, width: 28, objectFit: "contain", borderRadius: 6 }} onError={e => e.target.style.display = "none"} />
                  <div><div style={{ fontSize: 15, fontWeight: 700, color: co.color }}>{co.name}</div><div style={{ fontSize: 10, color: T.textMuted }}>{loaded ? `${data.length.toLocaleString()} trades loaded` : "No covering data"}</div></div>
                </div>
                <button onClick={() => ref.current?.click()} disabled={coverLoading[id]}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: `1px solid ${co.color}40`, background: loaded ? "transparent" : co.color + "20", color: co.color, cursor: coverLoading[id] ? "wait" : "pointer", fontSize: 12, fontWeight: 600, opacity: coverLoading[id] ? 0.6 : 1 }}>
                  {coverLoading[id] ? <><RefreshCw size={14} className="spin" /> Parsing...</> : <><Upload size={14} /> {loaded ? "Add More" : "Upload"}</>}
                </button>
                <input ref={ref} type="file" accept=".xlsx,.xls" onChange={e => handleCoverFile(id, e)} style={{ display: "none" }} />
              </div>
              {coverStatus[id] && (<div style={{ fontSize: 11, color: coverStatus[id].startsWith("✓") ? T.green : coverStatus[id].startsWith("✗") ? T.red : T.textDim, marginBottom: loaded ? 12 : 0 }}>{coverStatus[id]}</div>)}
              {loaded && (<>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 12 }}>
                  {[{ l: "Trades", v: data.length.toLocaleString() }, { l: "Clients", v: logins.length }, { l: "Months", v: monthBreakdown.length }].map(m => (
                    <div key={m.l} style={{ padding: "8px 10px", background: T.bg, borderRadius: 6 }}><div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase" }}>{m.l}</div><div style={{ fontSize: 16, fontWeight: 700, color: co.color }}>{m.v}</div></div>
                  ))}
                </div>
                {/* Month breakdown table */}
                <div style={{ background: T.bg, borderRadius: 8, overflow: "hidden" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr>
                      <th style={{ padding: "8px 10px", fontSize: 9, color: T.textMuted, fontWeight: 700, textTransform: "uppercase", textAlign: "left", borderBottom: `1px solid ${T.border}` }}>Month</th>
                      <th style={{ padding: "8px 10px", fontSize: 9, color: T.textMuted, fontWeight: 700, textTransform: "uppercase", textAlign: "center", borderBottom: `1px solid ${T.border}` }}>Trades</th>
                      <th style={{ padding: "8px 10px", fontSize: 9, color: T.textMuted, fontWeight: 700, textTransform: "uppercase", textAlign: "center", borderBottom: `1px solid ${T.border}` }}>Clients</th>
                      <th style={{ padding: "8px 10px", fontSize: 9, color: T.textMuted, fontWeight: 700, textTransform: "uppercase", textAlign: "center", borderBottom: `1px solid ${T.border}` }}></th>
                    </tr></thead>
                    <tbody>{monthBreakdown.map(mb => (
                      <tr key={`${mb.year}-${mb.month}`} style={{ borderBottom: `1px solid ${T.border}20` }}>
                        <td style={{ padding: "8px 10px", fontSize: 12, fontWeight: 600, color: T.text }}>{mb.month} {mb.year}</td>
                        <td style={{ padding: "8px 10px", fontSize: 12, color: co.color, fontWeight: 700, textAlign: "center" }}>{mb.trades.toLocaleString()}</td>
                        <td style={{ padding: "8px 10px", fontSize: 12, color: T.textDim, textAlign: "center" }}>{mb.clients.size}</td>
                        <td style={{ padding: "8px 10px", textAlign: "center" }}>
                          <button onClick={() => { if (window.confirm(`Delete ${mb.month} ${mb.year} (${mb.trades.toLocaleString()} trades)?`)) onCoveringDeleteMonth(id, mb.year, mb.month); }}
                            style={{ background: "none", border: `1px solid ${T.red}30`, borderRadius: 4, color: T.red, cursor: "pointer", padding: "3px 6px", fontSize: 10, opacity: 0.7 }}
                            onMouseEnter={e => e.currentTarget.style.opacity = 1} onMouseLeave={e => e.currentTarget.style.opacity = 0.7}>
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </>)}
            </Card>);
          })}
        </div>
        <Card accent={T.border}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 8 }}>Upload Instructions</div>
          <div style={{ fontSize: 11, color: T.textDim, lineHeight: 1.8 }}>
            • Upload the raw covering Excel file (with "Data" sheet)<br/>• Only essential columns kept (35 of 101)<br/>• You can upload multiple files — data accumulates<br/>
            • Both Al Salam and River Prime supported<br/>• Delete individual months using 🗑️ button<br/>• Go to <span style={{ color: "#fbbf24", fontWeight: 600 }}>Covering Gold</span> page to view the dashboard
          </div>
        </Card>
      </div>)}

      {/* ═══ SYSTEM EXCLUDE ═══ */}
      {tab === "exclude" && (<div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>System-Wide Exclude</div>
            <div style={{ fontSize: 11, color: T.textDim }}>Excluded CU/Login are removed from ALL calculations, charts, reports, and pages</div>
          </div>
          <span style={{ padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, color: excludeList.length > 0 ? "#ef4444" : "#22c55e", background: excludeList.length > 0 ? "rgba(239,68,68,0.12)" : "rgba(34,197,94,0.12)" }}>
            {excludeList.length} excluded
          </span>
        </div>

        {/* Add single */}
        <Card style={{ marginBottom: 14, overflow: "visible" }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Add to Exclude List</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 4 }}>
              {[["cu", "CU Account"], ["login", "Login"]].map(([v, l]) => (
                <button key={v} onClick={() => setExcludeType(v)} style={{ padding: "6px 14px", borderRadius: 6, border: excludeType === v ? "1px solid #06b6d4" : "1px solid " + T.border, background: excludeType === v ? "rgba(6,182,212,0.15)" : "transparent", color: excludeType === v ? "#06b6d4" : T.textDim, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{l}</button>
              ))}
            </div>
            <input value={excludeInput} onChange={e => setExcludeInput(e.target.value)} placeholder={excludeType === "cu" ? "e.g. CU8890" : "e.g. 100268"}
              onKeyDown={e => { if (e.key === "Enter" && excludeInput.trim()) { const val = excludeInput.trim(); if (!excludeList.find(x => x.type === excludeType && x.value === val)) { saveExclude([...excludeList, { type: excludeType, value: val, addedAt: new Date().toISOString().split("T")[0] }]); } setExcludeInput(""); } }}
              style={{ flex: 1, minWidth: 150, padding: "8px 12px", borderRadius: 6, border: "1px solid " + T.border, background: "#1a1e26", color: T.text, fontSize: 13, outline: "none", fontFamily: "monospace" }} />
            <button onClick={() => { const val = excludeInput.trim(); if (!val) return; if (!excludeList.find(x => x.type === excludeType && x.value === val)) { saveExclude([...excludeList, { type: excludeType, value: val, addedAt: new Date().toISOString().split("T")[0] }]); } setExcludeInput(""); }}
              style={{ padding: "8px 18px", borderRadius: 6, border: "1px solid #22c55e", background: "#22c55e", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>+ Add</button>
          </div>
          {/* Preview what will be excluded */}
          {excludeInput.trim() && (() => {
            const val = excludeInput.trim();
            const info = rawLookup[(excludeType === "cu" ? "cu_" : "login_") + val];
            const already = excludeList.find(x => x.type === excludeType && x.value === val);
            return (
              <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: 6, background: already ? "rgba(239,68,68,0.08)" : "rgba(6,182,212,0.08)", border: "1px solid " + (already ? "rgba(239,68,68,0.2)" : "rgba(6,182,212,0.2)"), fontSize: 11 }}>
                {already ? <span style={{ color: "#ef4444" }}>⚠ Already excluded</span>
                  : info ? <span style={{ color: T.textDim }}>{excludeType === "cu" ? "CU" : "Login"} <span style={{ color: "#06b6d4", fontWeight: 700, fontFamily: "monospace" }}>{val}</span> — {info.name || "Unknown"} ({info.company}){excludeType === "cu" && info.logins ? " — " + info.logins.size + " logins will be excluded" : ""}</span>
                  : <span style={{ color: "#f59e0b" }}>⚠ Not found in current data — will still be excluded if data appears later</span>}
              </div>
            );
          })()}
        </Card>

        {/* Bulk add */}
        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Bulk Add (paste multiple, one per line)</div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <textarea value={excludeBulk} onChange={e => setExcludeBulk(e.target.value)} rows={3} placeholder={"Paste " + (excludeType === "cu" ? "CU numbers" : "Login numbers") + " — one per line"}
              style={{ flex: 1, padding: "8px 12px", borderRadius: 6, border: "1px solid " + T.border, background: "#1a1e26", color: T.text, fontSize: 12, outline: "none", fontFamily: "monospace", resize: "vertical" }} />
            <button onClick={() => {
              const lines = excludeBulk.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
              if (!lines.length) return;
              const newList = [...excludeList];
              let added = 0;
              lines.forEach(val => { if (!newList.find(x => x.type === excludeType && x.value === val)) { newList.push({ type: excludeType, value: val, addedAt: new Date().toISOString().split("T")[0] }); added++; } });
              saveExclude(newList); setExcludeBulk("");
              alert("Added " + added + " new items (" + (lines.length - added) + " duplicates skipped)");
            }} style={{ padding: "8px 18px", borderRadius: 6, border: "1px solid #06b6d4", background: "rgba(6,182,212,0.15)", color: "#06b6d4", cursor: "pointer", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>Add All</button>
          </div>
        </Card>

        {/* Excluded list */}
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Excluded Items ({excludeList.length})</div>
            {excludeList.length > 0 && (
              <button onClick={() => { if (window.confirm("Remove ALL " + excludeList.length + " excluded items? This will make them visible again in the system.")) saveExclude([]); }}
                style={{ padding: "5px 12px", borderRadius: 5, border: "1px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.1)", color: "#ef4444", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>Clear All</button>
            )}
          </div>
          {excludeList.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: T.textMuted, fontSize: 13 }}>No excluded items — all data is visible</div>
          ) : (
            <div style={{ maxHeight: 400, overflowY: "auto" }}>
              <div style={{ display: "grid", gridTemplateColumns: "70px 100px 80px 1fr 80px 40px", gap: 8, padding: "6px 10px", fontSize: 9, color: T.textMuted, textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5, borderBottom: "1px solid " + T.border }}>
                <span>Type</span><span>Value</span><span>Company</span><span>Name</span><span>Added</span><span></span>
              </div>
              {excludeList.map((item, i) => {
                const info = rawLookup[(item.type === "cu" ? "cu_" : "login_") + item.value];
                const co = info?.company;
                const coColor = co === "salam" ? "#d4a017" : co === "river" ? "#3b82f6" : T.textMuted;
                const coLabel = co === "salam" ? "Al Salam" : co === "river" ? "River Prime" : "—";
                return (
                  <div key={item.type + "_" + item.value + "_" + i} style={{ display: "grid", gridTemplateColumns: "70px 100px 80px 1fr 80px 40px", gap: 8, alignItems: "center", padding: "8px 10px", borderBottom: "1px solid " + T.border + "20", fontSize: 12 }}>
                    <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, fontWeight: 700, textAlign: "center", color: item.type === "cu" ? "#f59e0b" : "#06b6d4", background: item.type === "cu" ? "rgba(245,158,11,0.12)" : "rgba(6,182,212,0.12)" }}>{item.type === "cu" ? "CU" : "LOGIN"}</span>
                    <span className="mono" style={{ fontWeight: 700, color: T.text }}>{item.value}</span>
                    <span style={{ fontSize: 10, fontWeight: 600, color: coColor }}>{coLabel}</span>
                    <span style={{ color: T.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11 }}>
                      {info ? info.name || "—" : <span style={{ color: "#f59e0b", fontSize: 10 }}>not in data</span>}
                      {info && item.type === "cu" && info.logins ? <span style={{ color: T.textMuted, fontSize: 9 }}> ({info.logins.size} logins)</span> : ""}
                    </span>
                    <span style={{ color: T.textMuted, fontSize: 10 }}>{item.addedAt}</span>
                    <button onClick={() => saveExclude(excludeList.filter((_, idx) => idx !== i))}
                      style={{ padding: "3px 6px", borderRadius: 4, border: "1px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.08)", color: "#ef4444", cursor: "pointer", fontSize: 10, fontWeight: 700 }}>✕</button>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>)}

      {/* ═══ REPORTS ═══ */}
      {tab === "reports" && (editingReport ? (
        <ReportDesigner
          editingReport={editingReport === "new" ? null : editingReport}
          onSave={(r) => {
            const configs = [...reportConfigs];
            const idx = configs.findIndex(c => c.id === r.id);
            if (idx >= 0) configs[idx] = r; else configs.push(r);
            setReportConfigs(configs); saveReportConfigs(configs); setEditingReport(null);
          }}
          onCancel={() => setEditingReport(null)}
        />
      ) : (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Report Templates</div>
              <div style={{ fontSize: 11, color: T.textDim }}>Create reports that appear on the Reports page for all users</div>
            </div>
            <button onClick={() => setEditingReport("new")} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 18px", borderRadius: 8, border: "1px solid " + T.green, background: T.green, color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
              <Plus size={14} /> New Report
            </button>
          </div>
          {reportConfigs.length === 0 ? (
            <Card style={{ padding: 50, textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.textDim, marginBottom: 6 }}>No Reports Yet</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Click "New Report" to create your first report template</div>
            </Card>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 12 }}>
              {reportConfigs.map(r => {
                const accent = r.level === "cu" ? "#f59e0b" : "#06b6d4";
                return (
                  <Card key={r.id} style={{ borderTop: "2px solid " + accent }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>{r.name}</div>
                        <div style={{ fontSize: 10, color: T.textDim, marginTop: 3 }}>{r.description || "No description"}</div>
                      </div>
                      <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, fontWeight: 700, color: accent, background: accent + "15" }}>{r.level === "cu" ? "CU" : "LOGIN"}</span>
                    </div>
                    <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginBottom: 10 }}>
                      {(r.columns || []).slice(0, 6).map(c => <span key={c.fieldId} style={{ padding: "2px 6px", borderRadius: 3, fontSize: 9, color: T.textDim, background: T.bgHover }}>{c.label}</span>)}
                      {(r.columns || []).length > 6 && <span style={{ fontSize: 9, color: T.textMuted }}>+{r.columns.length - 6}</span>}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 8, borderTop: "1px solid " + T.border }}>
                      <span style={{ fontSize: 9, color: T.textMuted }}>{(r.columns || []).length} cols · {(r.filters || []).length} filters · {r.createdAt}</span>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => { const dup = { ...r, id: "r_" + Date.now(), name: r.name + " (copy)" }; const c = [...reportConfigs, dup]; setReportConfigs(c); saveReportConfigs(c); }} style={{ padding: "4px 10px", borderRadius: 5, border: "1px solid " + T.border, background: "transparent", color: T.textDim, cursor: "pointer", fontSize: 10 }}><Copy size={11} /> Dup</button>
                        <button onClick={() => setEditingReport(r)} style={{ padding: "4px 10px", borderRadius: 5, border: "1px solid " + T.cyan, background: T.cyan + "15", color: T.cyan, cursor: "pointer", fontSize: 10, fontWeight: 600 }}><Edit3 size={11} /> Edit</button>
                        <button onClick={() => { if (window.confirm("Delete report: " + r.name + "?")) { const c = reportConfigs.filter(c => c.id !== r.id); setReportConfigs(c); saveReportConfigs(c); } }} style={{ padding: "4px 10px", borderRadius: 5, border: "1px solid " + T.red + "30", background: T.red + "10", color: T.red, cursor: "pointer", fontSize: 10, fontWeight: 600 }}><Trash2 size={11} /></button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
